import { useEffect, useState } from 'react';
import { useSocket } from '@/core/providers/SocketProvider';
import { ClientEvents } from '@voley/shared-types/socket';
import { presenceManager } from './manager';
import type { PresenceState } from './types';

export const useUserPresence = (userId: string | null) => {
  const [presence, setPresence] = useState(presenceManager.getUserPresence(userId || ''));
  
  useEffect(() => {
    if (!userId) return;
    
    const unsubscribe = presenceManager.subscribe((state: PresenceState) => {
      setPresence(state[userId]);
    });
    
    return () => {
      unsubscribe();
    };
  }, [userId]);
  
  return presence;
};

// Hook to get organization-wide presence
// Note: Presence events are handled globally by PresenceInitializer in SocketProvider.
// This hook only subscribes to presenceManager state changes - no duplicate event listeners.
export const useOrganizationPresence = () => {
  const [presenceState, setPresenceState] = useState<PresenceState>({});
  const { connected, emit } = useSocket();

  useEffect(() => {
    // Subscribe to presenceManager state changes
    // The actual socket events are handled by PresenceInitializer in SocketProvider
    const unsubscribe = presenceManager.subscribe(setPresenceState);
    return () => {
      unsubscribe();
    };
  }, []);

  return {
    presenceState,
    isUserOnline: (userId: string) => presenceState[userId]?.status === 'online',
    getUserLastSeen: (userId: string) => presenceState[userId]?.lastSeen,
    getUserStatus: (userId: string) => presenceState[userId]?.status || 'offline',
    // Method to subscribe to specific users
    subscribeToUsers: (userIds: string[]) => {
      if (connected && userIds.length > 0) {
        emit(ClientEvents.SUBSCRIBE_PRESENCE, { userIds });
      }
    }
  };
};